import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_BASE, api, getToken } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Eye, Loader2, ArrowLeft, Sparkles } from "lucide-react";

const GOALS = [
  { value: "express_feelings", label: "Express my feelings" },
  { value: "set_boundary", label: "Set a boundary" },
  { value: "apologize", label: "Apologize" },
  { value: "ask_question", label: "Ask a question" },
  { value: "resolve_conflict", label: "Resolve a conflict" },
  { value: "start_conversation", label: "Start a conversation" },
  { value: "end_conversation", label: "End a conversation" },
  { value: "professional", label: "Professional communication" },
  { value: "romantic", label: "Romantic communication" },
  { value: "other", label: "Other" },
];

const MAX_LEN = 5000;

export default function Composer() {
  const nav = useNavigate();
  const location = useLocation();
  const prefill = location.state?.prefill || "";
  const prefillRecipient = location.state?.recipient || "";
  const prefillGoalLabel = location.state?.goal || "";
  const initialGoalValue = GOALS.find((g) => g.label === prefillGoalLabel)?.value || "";
  const [message, setMessage] = useState(prefill);
  const [recipient, setRecipient] = useState(prefillRecipient);
  const [goal, setGoal] = useState(initialGoalValue);
  const [running, setRunning] = useState(false);
  const [preview, setPreview] = useState("");
  const abortRef = useRef(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const goalLabel = GOALS.find((g) => g.value === goal)?.label;

  const submit = async (e) => {
    e.preventDefault();
    if (running) return; // guard double-click
    if (!message.trim()) {
      toast.error("Please write a message first");
      return;
    }
    setRunning(true);
    setPreview("");

    const controller = new AbortController();
    abortRef.current = controller;
    // Client-side safety timeout so the UI never hangs forever on a stuck stream
    const timeoutId = setTimeout(() => controller.abort("timeout"), 90_000);

    let sawAnyEvent = false;

    try {
      const res = await fetch(`${API_BASE}/reviews/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          message: message.trim(),
          recipient: recipient.trim() || null,
          goal: goalLabel || null,
        }),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        const errText = await res.text().catch(() => "");
        let detail = errText;
        try { detail = JSON.parse(errText).detail || errText; } catch {/* keep raw */}
        throw new Error(detail || `Stream failed (${res.status})`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let carry = "";
      let finalId = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        carry += decoder.decode(value, { stream: true });
        const events = carry.split("\n\n");
        carry = events.pop() || "";
        for (const chunk of events) {
          const line = chunk.trim();
          if (!line.startsWith("data:")) continue;
          const jsonStr = line.slice(5).trim();
          if (!jsonStr) continue;
          let data;
          try { data = JSON.parse(jsonStr); } catch { continue; }
          sawAnyEvent = true;
          if (data.type === "delta") {
            setPreview((p) => p + (data.content || ""));
          } else if (data.type === "done") {
            finalId = data.review_id;
          } else if (data.type === "error") {
            throw new Error(data.message || "AI review failed");
          }
        }
      }

      if (finalId) {
        nav(`/app/reviews/${finalId}`, { replace: true });
        return;
      }

      // Fallback: the stream ended without a 'done' event (proxy termination,
      // network flake, etc.) but the server persists the review before emitting
      // 'done'. Fetch the user's newest review and navigate there so the user
      // never sees "nothing happens".
      // eslint-disable-next-line no-console
      console.warn("[FINALLOOK] Stream ended without a done event; falling back to latest review.", { sawAnyEvent });
      try {
        const { data: list } = await api.get("/reviews");
        const latest = Array.isArray(list) && list[0];
        if (latest?.id) {
          nav(`/app/reviews/${latest.id}`, { replace: true });
          return;
        }
      } catch (fbErr) {
        // eslint-disable-next-line no-console
        console.error("[FINALLOOK] Fallback fetch failed", fbErr);
      }
      throw new Error("The review finished but the app couldn't jump to it. Open Reviews to see it.");
    } catch (err) {
      if (err.name === "AbortError") {
        if (String(controller.signal.reason || "") === "timeout") {
          toast.error("Review is taking longer than expected. Please try again.");
        }
        return;
      }
      // eslint-disable-next-line no-console
      console.error("[FINALLOOK] RUN FINALLOOK failed:", err);
      toast.error(err.message || "Review failed. Please try again.");
    } finally {
      clearTimeout(timeoutId);
      setRunning(false);
      abortRef.current = null;
    }
  };

  return (
    <div data-testid="composer-screen">
      <button
        onClick={() => nav(-1)}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
        data-testid="composer-back-btn"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">Compose</p>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-1">Give it a FINALLOOK</h1>
      <p className="text-sm text-muted-foreground mb-8">
        Paste or type the message. Add context so the review is sharper.
      </p>

      <form onSubmit={submit} className="space-y-6" data-testid="composer-form">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="message">Your message</Label>
            <span
              className={`text-xs ${message.length > MAX_LEN ? "text-destructive" : "text-muted-foreground"}`}
              data-testid="composer-char-count"
            >
              {message.length}/{MAX_LEN}
            </span>
          </div>
          <Textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={MAX_LEN}
            placeholder="Type the message you&apos;re about to send…"
            className="min-h-[200px] text-base leading-relaxed resize-y"
            data-testid="composer-message-input"
            disabled={running}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="recipient">Recipient or context <span className="text-muted-foreground font-normal">(optional)</span></Label>
          <Input
            id="recipient"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="e.g., My manager, my partner after an argument, a close friend"
            className="h-11"
            data-testid="composer-recipient-input"
            disabled={running}
          />
        </div>

        <div className="space-y-2">
          <Label>What is your goal? <span className="text-muted-foreground font-normal">(optional)</span></Label>
          <Select value={goal} onValueChange={setGoal} disabled={running}>
            <SelectTrigger className="h-11" data-testid="composer-goal-trigger">
              <SelectValue placeholder="Select a goal" />
            </SelectTrigger>
            <SelectContent>
              {GOALS.map((g) => (
                <SelectItem key={g.value} value={g.value} data-testid={`composer-goal-${g.value}`}>
                  {g.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {running && (
          <div className="rounded-2xl border border-border bg-card p-5" data-testid="composer-preview">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-primary animate-pulse" />
              <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                {preview ? "Reading your message" : "Warming up…"}
              </span>
            </div>
            <p className="text-sm leading-relaxed text-foreground/90 whitespace-pre-wrap min-h-[3rem]">
              {preview}
              <span className="inline-block w-1.5 h-4 bg-primary/70 ml-0.5 align-middle animate-pulse" />
            </p>
          </div>
        )}

        <div className="pt-2">
          <Button
            type="submit"
            disabled={running || !message.trim()}
            className="w-full h-14 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-base tracking-wide"
            data-testid="composer-run-btn"
          >
            {running ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Reviewing…
              </>
            ) : (
              <>
                <Eye className="mr-2 h-5 w-5" />
                RUN FINALLOOK
              </>
            )}
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-3">
            Nothing is sent. You&apos;ll see suggestions and decide what to do.
          </p>
        </div>
      </form>
    </div>
  );
}
